import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Layout from '../../components/Layout';
import { Card } from '../../components/ui/card';
import { BookOpen, FileText, Users, CheckCircle } from 'lucide-react';
import api from '../../lib/api';

function AdminDashboard() {
  const navigate = useNavigate();
  const user = JSON.parse(localStorage.getItem('user') || '{}');
  const isFaculty = user.role === 'faculty';
  const [stats, setStats] = useState({
    questionBanks: 0,
    tests: 0,
    users: 0,
    attempts: 0,
  });
  const [activeTests, setActiveTests] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchStats();
    fetchActiveTests();
    
    // Refresh active tests every 30 seconds
    const interval = setInterval(fetchActiveTests, 30000);
    return () => clearInterval(interval);
  }, []);

  const fetchStats = async () => {
    try {
      const [banks, tests, users, dashStats] = await Promise.all([
        api.get('/question-banks'),
        api.get('/tests'),
        api.get('/users'),
        api.get('/dashboard/stats'),
      ]);
      setStats({
        questionBanks: banks.data.length,
        tests: tests.data.length,
        users: users.data.length,
        attempts: dashStats.data.attempts || 0,
      });
    } catch (error) {
      console.error('Error fetching stats:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchActiveTests = async () => {
    try {
      const response = await api.get('/dashboard/active-tests');
      setActiveTests(response.data);
    } catch (error) {
      console.error('Error fetching active tests:', error);
    }
  };

  const statCards = [
    {
      title: 'Question Banks',
      value: stats.questionBanks,
      icon: BookOpen,
      color: 'bg-blue-100 text-blue-600',
      route: '/admin/question-banks',
    },
    {
      title: 'Tests',
      value: stats.tests,
      icon: FileText,
      color: 'bg-green-100 text-green-600',
      route: '/admin/tests',
    },
    {
      title: 'Users',
      value: stats.users,
      icon: Users,
      color: 'bg-purple-100 text-purple-600',
      route: '/admin/users',
    },
    {
      title: 'Attempts',
      value: stats.attempts,
      icon: CheckCircle,
      color: 'bg-orange-100 text-orange-600',
      route: '/admin/analytics',
    },
  ];

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-96">
          <div className="spinner"></div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="max-w-7xl mx-auto">
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-2" data-testid="admin-dashboard-title">
            {isFaculty ? 'Faculty Dashboard' : 'Admin Dashboard'}
          </h1>
          <p className="text-gray-600">Overview of your testing platform</p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          {statCards.map((stat) => {
            const Icon = stat.icon;
            return (
              <Card 
                key={stat.title} 
                className="stat-card cursor-pointer hover:shadow-lg transition-all hover:scale-105" 
                data-testid={`stat-card-${stat.title.toLowerCase().replace(/\s+/g, '-')}`}
                onClick={() => navigate(stat.route)}
              >
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-gray-600 mb-1">{stat.title}</p>
                    <p className="text-3xl font-bold text-gray-900">{stat.value}</p>
                  </div>
                  <div className={`icon-wrapper ${stat.color}`}>
                    <Icon className="w-6 h-6" />
                  </div>
                </div>
              </Card>
            );
          })}
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <Card className="p-6">
            <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
              <span className="inline-block w-3 h-3 bg-green-500 rounded-full animate-pulse"></span>
              Live Tests in Progress
            </h3>
            {activeTests.length === 0 ? (
              <p className="text-gray-500 text-sm">No active tests at the moment</p>
            ) : (
              <div className="space-y-3">
                {activeTests.map((batch) => (
                  <div key={batch.batch_id} className="flex items-center justify-between p-3 bg-green-50 rounded-lg border border-green-200">
                    <div>
                      <p className="font-medium text-gray-900">{batch.batch_name}</p>
                      {batch.batch_year && (
                        <p className="text-sm text-gray-500">Year: {batch.batch_year}</p>
                      )}
                    </div>
                    <div className="text-right">
                      <p className="text-2xl font-bold text-green-600">{batch.active_users}</p>
                      <p className="text-xs text-gray-600">active</p>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </Card>
          <Card className="p-6">
            <h3 className="text-lg font-semibold mb-4">Quick Actions</h3>
            <div className="space-y-2">
              <a href="/admin/question-banks" className="block p-3 rounded-lg hover:bg-gray-50 transition-colors" data-testid="quick-action-create-bank">
                <p className="font-medium text-gray-900">Create Question Bank</p>
                <p className="text-sm text-gray-500">Add a new question repository</p>
              </a>
              <a href="/admin/tests/create" className="block p-3 rounded-lg hover:bg-gray-50 transition-colors" data-testid="quick-action-create-test">
                <p className="font-medium text-gray-900">Create Test</p>
                <p className="text-sm text-gray-500">Build a new assessment</p>
              </a>
            </div>
          </Card>
        </div>
      </div>
    </Layout>
  );
}

export default AdminDashboard;